import { Field, ErrorMessage } from "formik";
import { Form } from "react-bootstrap";
import { useState, useMemo, useCallback } from "react";
import ChooseLocation from "./ChooseLocation";
import { useTranslation } from "react-i18next";
import { MapIcon, CrosshairIcon } from "@components/icons";

function LocationComponent({ onPositionChange }) {
  const [error, setError] = useState<string | null>(null);

  const findCurrentPosition = () => {
    if (!navigator.geolocation) {
      setError("Geolocation is not supported by your browser");
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        onPositionChange({
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        });
      },
      () => {
        setError("Unable to retrieve your location");
      }
    );
  };

  return (
    <button
      onClick={findCurrentPosition}
      className="btn btn-primary btn-sm mt-auto"
      title="Get current Location"
      type="button"
    >
      <CrosshairIcon />
      {error && <p>{error}</p>}
    </button>
  );
}

export default function TreeLocation({
  values,
  formField,
  validation,
  setFieldValue,
  readOnly = false,
}) {
  const [showMap, setShowMap] = useState(true);
  const { latitude, longitude, altitude } = formField;
  const { t } = useTranslation();
  const valid = validation(t);

  const mapPosition = useMemo(() => {
    const latNum = Number(values?.latitude);
    const lngNum = Number(values?.longitude);
    if (Number.isFinite(latNum) && Number.isFinite(lngNum)) {
      return { latlng: { lat: latNum, lng: lngNum } };
    }
    return { latlng: { lat: "", lng: "" } };
  }, [values?.latitude, values?.longitude]);

  const handleMapPick = useCallback(
    (latlng) => {
      if (!latlng) return;
      if (
        String(values?.latitude) === String(latlng.lat) &&
        String(values?.longitude) === String(latlng.lng)
      ) {
        return;
      }
      setFieldValue("latitude", latlng.lat);
      setFieldValue("longitude", latlng.lng);
    },
    [setFieldValue, values?.latitude, values?.longitude]
  );

  return (
    <>
      <div className="d-flex align-items-center gap-2">
        <button
          type="button"
          className="btn btn-primary btn-sm mt-auto"
          title="Choose position on map"
          onClick={() => setShowMap(!showMap)}
        >
          <MapIcon />
        </button>

        <div className="d-flex flex-1 align-items-center gap-2">
          <Form.Group className="flex-fill">
            <Form.Label htmlFor="">{t(latitude.label) + "*"} </Form.Label>
            <Field
              type="number"
              name={latitude.name}
              validate={(value) => valid.positionValidation(value, "latitude")}
              className="form-control"
              placeholder={"e.g.: 12.34"}
              min="-90"
              max="90"
              step="any"
            />
          </Form.Group>
          <Form.Group className="flex-fill">
            <Form.Label htmlFor="">{t(longitude.label) + "*"} </Form.Label>
            <Field
              type="number"
              name={longitude.name}
              validate={(value) => valid.positionValidation(value, "longitude")}
              className="form-control"
              placeholder={"e.g.: 12.34"}
              min="-180"
              max="180"
              step="any"
            />
          </Form.Group>
          <Form.Group className="flex-fill">
            <Form.Label htmlFor="">{t(altitude.label)}</Form.Label>
            <Field type="number" name={altitude.name} className="form-control" />
          </Form.Group>
        </div>
        <LocationComponent onPositionChange={handleMapPick} />
      </div>
      <ChooseLocation
        showMap={showMap}
        setPosition={handleMapPick}
        setShowMap={setShowMap}
        mapPosition={mapPosition}
        values={values}
        zoom={1}
        readOnly={readOnly}
      />
    </>
  );
}
